import * as R from 'remeda';
import { IconGripVertical } from '@rossum/ui/icons/tabler';
import { Box, BoxProps, SvgIcon } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { usePageSpaceContext } from '../../features/annotation-view/document-canvas-svg/page/PageSpaceContext';
import {
  DRAG_HANDLE_SIZE,
  GRID_LINE_WIDTH,
  LABEL_HORIZONTAL_PADDING,
} from './constants';
import { useGridContext } from './GridContext';
import { useDraggableInteraction } from './useDraggableInteraction';
import { moveGridByGridHandle } from './utils';

export type GridDragHandleProps = Omit<
  BoxProps,
  'onDrag' | 'onDragStart' | 'onDragEnd'
> & {
  onMoved: () => void;
};

export const GridDragHandle = ({
  onMoved,
  ...restBoxProps
}: GridDragHandleProps) => {
  const { pageWidth, pageHeight } = usePageSpaceContext();

  const {
    gridState,
    gridDraftState: [_, setDraftState],
    interactionInProgress,
  } = useGridContext();

  const { isDragging, handleMouseDown } = useDraggableInteraction({
    onDragStart: () => setDraftState(gridState),
    onDrag: diff =>
      setDraftState(
        moveGridByGridHandle(gridState, [
          R.clamp(Math.round(diff[0]), {
            min: -(gridState.columns[0]?.leftPosition ?? 0),
            max:
              pageWidth -
              gridState.width -
              (gridState.columns[0]?.leftPosition ?? 0),
          }),
          R.clamp(Math.round(diff[1]), {
            min: -(gridState.rows[0]?.topPosition ?? 0),
            max:
              pageHeight -
              gridState.height -
              (gridState.rows[0]?.topPosition ?? 0),
          }),
        ])
      ),
    onDragEnd: () => {
      onMoved();
      setDraftState(null);
    },
  });

  return (
    <Box
      {...restBoxProps}
      onMouseDownCapture={handleMouseDown}
      sx={{
        width: DRAG_HANDLE_SIZE,
        height: DRAG_HANDLE_SIZE,
        position: 'absolute',
        backgroundColor: theme => theme.palette.primary.main,
        borderRadius: borderRadius('s'),
        color: theme => theme.palette.primary.contrastText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...restBoxProps.sx,
      }}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        pointerEvents: interactionInProgress && !isDragging ? 'none' : 'all',

        left:
          -DRAG_HANDLE_SIZE - GRID_LINE_WIDTH / 2 - LABEL_HORIZONTAL_PADDING,
        top: -DRAG_HANDLE_SIZE - GRID_LINE_WIDTH / 2 - LABEL_HORIZONTAL_PADDING,
        ...restBoxProps.style,
      }}
    >
      <SvgIcon fontSize="medium">
        <IconGripVertical />
      </SvgIcon>
    </Box>
  );
};
